import errorService from '../errors/errorService';
import {MW_ERRORS} from '../../consts/app/mwErrors';
import iotService from './iotService';
import {selectUamAssetId} from '../../state/selectors/uamData';
import UamClient from '../uam/uamClient';
import * as uamMethods from '../../consts/uam/uamMethods';
import {log} from '../log';
import {setConnectedToCm, setFwuInProgress} from '../../state/slices/iotData';
import {selectIsFwuInProgress} from '../../state/selectors/iotData';
import {subscribeToSocketsAndWaitConnected} from './iotConnectionService';
import uamFlowService from '../uam/uamFlowService';
import * as uamFlowTypes from '../../consts/uam/uamFlowTypes';
import uamTimeouts from '../../consts/uam/uamTimeouts';

export const getIotFwuAvailability = async (isHolder) => {
    const assetId = selectUamAssetId(isHolder);

    if (!assetId) throw errorService.createError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

    try {
        const firmwareResponse = await new UamClient().callGet({
            methodName: uamMethods.GET_FIRMWARE_AVAILABILITY,
            requestConfig: {args: assetId},
            isHolder,
        });

        log.info(`Firmware availability. isHolder: ${isHolder}, response: ${JSON.stringify(firmwareResponse)}`);

        if (firmwareResponse) return firmwareResponse;
    } catch (e) {
        throw iotService.getCustomIotError(e);
    }

    throw errorService.createError(MW_ERRORS.ERROR_IOT_NO_FWU_INFO, 'Fwu Availability response is not valid');
};

let onFwuSuccessHandler;
let onFwuErrorHandler;

export const startIotFwu = async ({isHolder, data, onSuccess, onError}) => {
    onFwuSuccessHandler = onSuccess;
    onFwuErrorHandler = onError;

    const onFwuError = (code, message) => {
        const error = errorService.createError(code, message);

        setFwuInProgress(isHolder, false, error);
        return false;
    };

    const {firmwareVersion, engineFirmwareVersion, bleFirmwareVersion} = data;
    const requestData = getFwuRequestData(firmwareVersion, engineFirmwareVersion, bleFirmwareVersion);

    if (!requestData) return onFwuError(MW_ERRORS.ERROR_WRONG_DATA, 'Provided data is wrong');

    const isFwuInProgress = selectIsFwuInProgress(isHolder);

    if (isFwuInProgress) return onFwuError(MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS, 'Already in progress');

    const assetId = selectUamAssetId(isHolder);

    if (!assetId) return onFwuError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

    try {
        //forced reconnect to CM to check if sbl mode or not
        setConnectedToCm(isHolder, false);

        await subscribeToSocketsAndWaitConnected(isHolder);

        setFwuInProgress(isHolder, true);
        log.info(
            `Start firmwareUpdate process. isHolder: ${!!isHolder}. firmwareVersion: ${firmwareVersion}, engineFirmwareVersion: ${engineFirmwareVersion}`
        );

        const {requestId} =
            (await new UamClient().callPost({
                methodName: uamMethods.UPDATE_FIRMWARE,
                requestConfig: {args: assetId, data: requestData},
                isHolder,
            })) || {};

        if (!requestId) return onFwuError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'Request id is not defined');

        uamFlowService.createFlow({
            type: uamFlowTypes.UPDATE_FIRMWARE,
            timeoutInSec: uamTimeouts.UAM_FIRMWARE_TIMEOUT,
            isHolder,
            requestId,
        });

        return true;
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setFwuInProgress(isHolder, false, customError);
    }
};

const getFwuRequestData = (firmwareVersion, engineFirmwareVersion, bleFirmwareVersion) => {
    if (!firmwareVersion && !engineFirmwareVersion && !bleFirmwareVersion) return;

    const data = {};

    if (firmwareVersion) {
        data.firmwareVersion = firmwareVersion;
    }

    const associatedFirmwareVersions = {};

    if (bleFirmwareVersion) {
        associatedFirmwareVersions.BLE = {
            firmwareVersion: bleFirmwareVersion,
        };
    }

    if (engineFirmwareVersion) {
        associatedFirmwareVersions.engine = {
            firmwareVersion: engineFirmwareVersion,
        };
    }

    data.associatedFirmwareVersions = associatedFirmwareVersions;

    return data;
};

export const onFwuFinish = (isHolder, error) => {
    const status = error ? 'Failed' : 'Success';
    log.info(
        `End firmwareUpdate process. ${status}. isHolder: ${isHolder}.${!!error ? ' error: ' + JSON.stringify(error) + '.' : ''}`
    );

    return error ? onFwuErrorHandler?.(error) : onFwuSuccessHandler?.();
};
